// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/estoque/hooks/useInventario.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inventarioService, type FiltrosInventario } from '../services/inventarioService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { CriarInventarioRequest, LancarContagemRequest } from '../types';

const KEYS = {
  all:     ['inventarios'] as const,
  lista:   (pagina: number, tamanhoPagina: number, filtros: FiltrosInventario) =>
    ['inventarios', 'lista', pagina, tamanhoPagina, filtros] as const,
  detalhe: (id: number) => ['inventarios', id] as const,
};

interface UseInventarioParams {
  pagina?: number;
  tamanhoPagina?: number;
  /** Busca, situação, período e ordenação (servidor). */
  filtros?: FiltrosInventario;
}

export const useInventario = ({
  pagina = 1,
  tamanhoPagina = 20,
  filtros = {},
}: UseInventarioParams = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: KEYS.lista(pagina, tamanhoPagina, filtros),
    queryFn:  () => inventarioService.getAll(pagina, tamanhoPagina, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada filtro
  });

  const inventarios  = paginado?.itens       ?? [];
  const total        = paginado?.total        ?? 0;
  const totalPaginas = paginado?.totalPaginas ?? 1;

  const createMutation = useMutation({
    mutationFn: (req: CriarInventarioRequest) => inventarioService.create(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      toast.success('Inventário criado com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const lancarMutation = useMutation({
    mutationFn: ({ id, req }: { id: number; req: LancarContagemRequest }) =>
      inventarioService.lancarContagem(id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      toast.success('Contagem lançada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const processarMutation = useMutation({
    mutationFn: (id: number) => inventarioService.processar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      toast.success('Inventário processado com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => inventarioService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      toast.success('Inventário excluído com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    inventarios,
    total,
    totalPaginas,
    isLoading,
    isFetching,
    create:           createMutation.mutateAsync,
    lancarContagem:   lancarMutation.mutateAsync,
    processar:        processarMutation.mutateAsync,
    deleteInventario: deleteMutation.mutate,
    isCreating:       createMutation.isPending,
    isProcessando:    processarMutation.isPending,
  };
};

export const useInventarioDetalhe = (id: number) =>
  useQuery({
    queryKey: KEYS.detalhe(id),
    queryFn:  () => inventarioService.getById(id),
    enabled:  id > 0,
  });
